import { Dispatch, SetStateAction, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Button, Container, styled } from '@mui/material';
import styles from '../common/styles/util.module.scss';
import { Phone } from './Phone';
import { UpdateMailTo } from './UpdateMailTo';
import {
  getUpdateUserProfile,
  IAddress,
  isUserProfileUpdateComplete,
  IUser,
  normalizePhone,
  updateUser,
  useAlertContext,
  useUserContext,
} from '../common';
import { assocPath, compose, defaultTo, pathOr } from 'rambda';
import { compose as lcompose } from 'lodash/fp';
import { fold } from 'fp-ts/lib/Either';
import { flow } from 'fp-ts/function';

export function UpdateUserProfileDetails({
  user,
  setEdit,
}: {
  user: IUser;
  setEdit: Dispatch<SetStateAction<boolean>>;
}) {
  const [{ phone, address }, setDetails] = useState<{
    address: IAddress;
    phone: string | null;
  }>({ phone: user.phone, address: user.address });

  const setDetailsAt = (property: string) =>
    compose(setDetails, assocPath(property), defaultTo(''));

  const { setAlert } = useAlertContext();
  const { setUser } = useUserContext();

  const handleSubmit = flow(
    () =>
      setAlert(a => ({
        ...a,
        title,
        message: () => (
          <>
            <FontAwesomeIcon icon={faSpinner} size="1x" fixedWidth spin />{' '}
            pending
          </>
        ),
        cancelAction: false,
        isOpen: true,
      })),
    () =>
      flow(
        getUpdateUserProfile,
        updateUser(
          lcompose(
            pathOr(
              {
                'content-type': '',
                authorization: '',
                'x-csrf-csos-token': '',
              },
              'icam.headers'
            )
          )(user)
        )
      )(user, normalizePhone(phone), address).then((a: any) => {
        fold(
          flow(() =>
            setAlert(a => ({
              ...a,
              title,
              message: () => <>user profile update error.</>,
              okAction: true,
              cancelAction: false,
              isOpen: true,
            }))
          ),
          flow(
            () =>
              setUser(a => ({ ...a, phone: normalizePhone(phone), address })),
            () =>
              setAlert(a => ({
                ...a,
                title,
                message: () => <>user profile update success.</>,
                okAction: true,
                cancelAction: false,
                isOpen: true,
              })),
            () => setEdit(false)
          )
        )(a);
      })
  );

  return (
    <DetailsWrapper>
      <Container maxWidth="lg">
        <fieldset>
          <Wrapper>
            <Name> Email :</Name>
            <span>{user.loginEmail}</span>

            <Phone phoneFromDetails={phone} onChange={setDetailsAt('phone')} />
          </Wrapper>
        </fieldset>
      </Container>

      <UpdateMailTo addressFromDetails={address} setDetails={setDetails} />

      <Container maxWidth="lg">
        <p>Note: Please make sure to enter your business mailing address.</p>
      </Container>

      <section className={styles['flex-space-evenly']}>
        <Button
          onClick={flow(handleSubmit)}
          disabled={!isUserProfileUpdateComplete({ phone, address })}
          variant="contained"
          color="primary">
          submit
        </Button>

        <Button
          onClick={() => setEdit(false)}
          variant="contained"
          color="secondary">
          Cancel
        </Button>
      </section>
    </DetailsWrapper>
  );
}

const title = `CSOS Update User Profile`;

const DetailsWrapper = styled('form')(({ theme }) => ({
  '& > *+*': {
    marginTop: `2rem`,
  },
}));

const Wrapper = styled('article')(({ theme }) => ({
  display: 'grid',
  gap: `1rem`,
  alignItems: 'center',

  gridTemplateColumns: '1fr',

  '& > label': {
    justifySelf: 'start',
  },

  '@media (min-width: 80em)': {
    gridTemplateColumns: '1fr 4fr',

    '& > label': {
      justifySelf: 'end',
    },
  },
}));

const Name = styled('label')(({ theme }) => ({ fontWeight: 700 }));
